import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";

function useIsMobile(breakpoint = 760) {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.innerWidth <= breakpoint;
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= breakpoint);
        };

        handleResize();
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, [breakpoint]);

    return isMobile;
}

export default function AdminLittersPage({
                                             primaryColor = "#8D6E63",
                                             secondaryColor = "#A1887F",
                                         }) {
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [litters, setLitters] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const responsiveStyles = useMemo(
        () => ({
            headerActions: {
                display: "flex",
                gap: "0.75rem",
                flexWrap: "wrap",
                width: isMobile ? "100%" : "auto",
            },

            headerButton: {
                flex: isMobile ? "1 1 100%" : "0 0 auto",
                textAlign: "center",
            },

            litterCard: {
                ...adminStyles.dashboardCard,
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) auto",
                gap: isMobile ? "1.25rem" : "1rem",
                alignItems: isMobile ? "stretch" : "center",
                padding: isMobile ? "1rem" : adminStyles.dashboardCard.padding,
            },

            actions: {
                display: "grid",
                gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(4, max-content)",
                gap: "0.5rem",
                justifyContent: isMobile ? "stretch" : "flex-end",
                alignItems: "center",
            },

            actionButton: {
                width: isMobile ? "100%" : "auto",
                textAlign: "center",
                whiteSpace: "nowrap",
            },
        }),
        [isMobile]
    );

    useEffect(() => {
        loadLitters();
    }, []);

    async function loadLitters() {
        setIsLoading(true);
        setErrorMessage("");

        const { data, error } = await supabase
            .from("litters")
            .select(
                `
                *,
                puppies (id),
                mother:mother_id (id, name),
                father:father_id (id, name)
            `
            )
            .order("sort_order", { ascending: true })
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Fehler beim Laden der Würfe:", error);
            setErrorMessage("Die Würfe konnten nicht geladen werden.");
            setIsLoading(false);
            return;
        }

        setLitters(data || []);
        setIsLoading(false);
    }

    async function handleToggleVisible(litter) {
        const nextVisibleState = !litter.is_visible;

        const { error } = await supabase
            .from("litters")
            .update({
                is_visible: nextVisibleState,
                updated_at: new Date().toISOString(),
            })
            .eq("id", litter.id);

        if (error) {
            console.error("Fehler beim Aktualisieren:", error);
            alert("Der Wurf konnte nicht aktualisiert werden.");
            return;
        }

        setLitters((current) =>
            current.map((item) =>
                item.id === litter.id
                    ? {
                        ...item,
                        is_visible: nextVisibleState,
                        updated_at: new Date().toISOString(),
                    }
                    : item
            )
        );
    }

    async function handleDelete(id) {
        const confirmed = window.confirm(
            "Möchtest du diesen Wurf wirklich löschen? Zugehörige Welpen werden ebenfalls gelöscht."
        );

        if (!confirmed) return;

        const { error } = await supabase.from("litters").delete().eq("id", id);

        if (error) {
            console.error("Fehler beim Löschen:", error);
            alert("Der Wurf konnte nicht gelöscht werden.");
            return;
        }

        setLitters((current) => current.filter((item) => item.id !== id));
    }

    function formatDate(value) {
        if (!value) return "Kein Geburtsdatum";

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    }

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            Würfe verwalten
                        </h1>
                        <p style={adminStyles.subtitle}>
                            A-Wurf, B-Wurf und weitere Würfe erstellen, bearbeiten und veröffentlichen.
                        </p>
                    </div>

                    <div style={responsiveStyles.headerActions}>
                        <button
                            type="button"
                            onClick={() => navigate("/admin")}
                            style={{
                                ...adminStyles.secondaryButton,
                                ...responsiveStyles.headerButton,
                                backgroundColor: secondaryColor,
                            }}
                        >
                            Dashboard
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/litters/new")}
                            style={{
                                ...adminStyles.secondaryButton,
                                ...responsiveStyles.headerButton,
                                backgroundColor: primaryColor,
                            }}
                        >
                            Neuer Wurf
                        </button>
                    </div>
                </header>

                {errorMessage && <div style={adminStyles.error}>{errorMessage}</div>}

                {isLoading ? (
                    <p style={{ color: "#666" }}>Würfe werden geladen...</p>
                ) : litters.length === 0 ? (
                    <motion.div
                        style={adminStyles.dashboardCard}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <h2 style={{ marginTop: 0, color: primaryColor }}>
                            Noch keine Würfe vorhanden
                        </h2>

                        <p style={{ color: "#666", lineHeight: 1.7 }}>
                            Lege den ersten Wurf an. Danach kannst du einzelne Welpen hinzufügen.
                        </p>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/litters/new")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                marginTop: "1rem",
                                width: isMobile ? "100%" : "auto",
                            }}
                        >
                            Wurf erstellen
                        </button>
                    </motion.div>
                ) : (
                    <div style={{ display: "grid", gap: "1rem" }}>
                        {litters.map((litter, index) => (
                            <motion.article
                                key={litter.id}
                                style={responsiveStyles.litterCard}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.35,
                                    delay: index * 0.04,
                                }}
                            >
                                <div style={{ minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            flexWrap: "wrap",
                                            marginBottom: "0.75rem",
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "inline-block",
                                                padding: "0.3rem 0.75rem",
                                                borderRadius: "999px",
                                                backgroundColor: litter.is_visible
                                                    ? `${primaryColor}18`
                                                    : "#f1f1f1",
                                                color: litter.is_visible
                                                    ? primaryColor
                                                    : "#777",
                                                fontSize: "0.85rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {litter.is_visible ? "Sichtbar" : "Ausgeblendet"}
                                        </span>

                                        <span
                                            style={{
                                                display: "inline-block",
                                                padding: "0.3rem 0.75rem",
                                                borderRadius: "999px",
                                                backgroundColor: "#f7f7f7",
                                                color: "#666",
                                                fontSize: "0.85rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {litter.puppies?.length || 0} Welpen
                                        </span>
                                    </div>

                                    <h2
                                        style={{
                                            margin: "0 0 0.5rem",
                                            color: primaryColor,
                                            fontSize: "clamp(1.25rem, 4vw, 1.5rem)",
                                            lineHeight: 1.35,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {litter.name}
                                    </h2>

                                    <p
                                        style={{
                                            margin: "0 0 0.5rem",
                                            color: "#666",
                                            lineHeight: 1.6,
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        Geboren am: {formatDate(litter.birth_date)}
                                    </p>

                                    <p
                                        style={{
                                            margin: "0 0 0.5rem",
                                            color: "#666",
                                            lineHeight: 1.6,
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        Eltern: {litter.mother?.name || "Keine Mutter gewählt"} &{" "}
                                        {litter.father?.name || "Kein Vater gewählt"}
                                    </p>

                                    <p
                                        style={{
                                            margin: 0,
                                            color: "#888",
                                            fontSize: "0.9rem",
                                        }}
                                    >
                                        Sortierung: {litter.sort_order ?? 0}
                                    </p>
                                </div>

                                <div style={responsiveStyles.actions}>
                                    <button
                                        type="button"
                                        onClick={() => handleToggleVisible(litter)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            ...responsiveStyles.actionButton,
                                            backgroundColor: litter.is_visible
                                                ? "#777"
                                                : primaryColor,
                                        }}
                                    >
                                        {litter.is_visible ? "Ausblenden" : "Einblenden"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(`/admin/litters/${litter.id}/edit`)
                                        }
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            ...responsiveStyles.actionButton,
                                            backgroundColor: secondaryColor,
                                        }}
                                    >
                                        Bearbeiten
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(`/admin/litters/${litter.id}/puppies`)
                                        }
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            ...responsiveStyles.actionButton,
                                            backgroundColor: primaryColor,
                                        }}
                                    >
                                        Welpen
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleDelete(litter.id)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            ...responsiveStyles.actionButton,
                                            backgroundColor: "#b94a48",
                                        }}
                                    >
                                        Löschen
                                    </button>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}